import type {
  Delivery,
  DeliveryBoardStatus,
  IDeliverySource,
  IDeliverySourceKey,
  InventoryLevel,
  SiteLogistics,
} from '../interfaces/ILogistics.js';
import { UnauthorizedError } from './errors.js';
import { createLogisticsWrite, type ILogisticsRevisionHandle } from './logisticsRevision.js';
import type { AuthedFetch } from './transport.js';
import { supplierRefusalOf, type ISupplierBody, type ISupplierPatch } from './suppliers.js';
import {
  apiFailure,
  conflictReasonOf,
  DeliveryOrderConflictError,
  LogisticsNotFoundError,
  readLogisticsFailure,
  StatusInUseError,
  type ILogisticsFailure,
} from './logisticsErrors.js';

export * from './logisticsErrors.js';

/** Thrown when the site exists but has no linked project (HTTP 409, SITE_NOT_LINKED). */
export class LogisticsSiteNotLinkedError extends Error {
  constructor() {
    super('logistics: site not linked to a project');
    this.name = 'LogisticsSiteNotLinkedError';
  }
}

/** Shared retry policy for the site-logistics query: deterministic failures —
 *  missing site, unlinked site, auth — never retry; transient ones get two attempts. */
export function logisticsQueryRetry(failureCount: number, err: unknown): boolean {
  return err instanceof LogisticsNotFoundError ||
    err instanceof LogisticsSiteNotLinkedError ||
    err instanceof UnauthorizedError
    ? false
    : failureCount < 2;
}

/** Snapshot write body — mirrors the backend's `SnapshotBodySchema`. */
export type ISnapshotBody = {
  materialName: string;
  /** PATCH semantics: on an update of an existing material, omitted fields
   *  are untouched and only what is sent gets written. The server re-anchors
   *  the tank's clock only when `amount` is present — a metadata-only edit
   *  must not carry it. A material's FIRST write requires all three. */
  unit?: string;
  amount?: number;
  spanDays?: number;
  /** Tri-state: omitted = untouched, null = cleared, number = the estimate. */
  estimatedLeadTimeDays?: number | null;
  /** 'sync' reconciles the stock against reality — closes the open cycle on
   *  what was consumed, opens a new count. Omitted for a plain add/re-estimate. */
  mode?: 'update' | 'sync';
  /** Required by 'sync': the user's own count of the old stock. The server
   *  closes the cycle on `open.amount − remaining`, so this is what makes it a
   *  measurement rather than a replay of the estimate. */
  remaining?: number;
  /** Sync only: what was lost out of the closing batch, in the stock's unit.
   *  Recorded on the cycle the sync closes. */
  waste?: number;
  /** Orders this count reconciles that are recorded as arrived in the SAME
   *  request — written by the server only once the count is known to be
   *  acceptable, and just before it. */
  arrivals?: string[];
};

/** One material on the order, as a write body carries it. `id` names an
 *  EXISTING line on a patch (so a rename is a change rather than a
 *  remove-plus-add in the order's history); omitted, the line is new.
 *
 *  `source` is CREATE-only (contract v14): the Scheduler task this line is
 *  raised for and its share of it — the claim's need. Sent per line by the
 *  Stock tab's checkout, which can put several of one task's materials on
 *  one truck; the server stores it as that line's first `sources` entry.
 *  Patch bodies never carry it. */
export type DeliveryOrderLineBody = {
  id?: string;
  itemName: string;
  quantity: number;
  unit: string;
  source?: IDeliverySource;
};

/** Write body for D-ORCA-owned delivery orders. Optional nullable fields mirror
 *  the backend's DeliveryOrderBodySchema; `deliveredAt` is service-stamped and
 *  never client-set.
 *
 *  No status of any kind: a new order always starts in the board's first column
 *  and the server derives its state. The UI has exactly one status vocabulary —
 *  the site's own board columns — and there is nothing to say about a delivery
 *  that has not been raised yet. */
export type DeliveryOrderBody = {
  destinationName: string;
  /** What the truck carries — 1..`MAX_DELIVERY_LINES`, folded names distinct.
   *  The server refuses an empty array; the form refuses to remove the last
   *  row for the same reason (delete the order instead). */
  lines: DeliveryOrderLineBody[];
  deliveryOrder?: string | null;
  requestedDate?: string | null;
  answeredDate?: string | null;
  dueDate?: string | null;
  /** The order's supplier. On a patch `null` clears it and omitted leaves it
   *  alone; a create just omits it for none. Never beside `newSupplier`. */
  supplierId?: string | null;
  /** A supplier saved with this write and put on the order (spec 2026-10-25
   *  §12) — a name the site has is a `SupplierNameTakenError`, a bad phone a
   *  `SupplierPhoneInvalidError`, and no order is written. */
  newSupplier?: ISupplierBody;
  /** Changes to the supplier the order points at after this write; only the
   *  fields that changed. */
  supplierEdit?: ISupplierPatch;
};

/**
 * A patch. One status-shaped key, because the UI has exactly one status:
 *
 * `status` names the site's own board column and moves the card between them.
 * Moving it into the `delivered` system column is how an arrival is recorded —
 * the server stamps `deliveredAt` — and moving it back out clears the stamp, so
 * an arrival is reversible. The key carries nothing else: Genba's six-state
 * workflow enum is gone from the contract, and the arrival-only escape hatch
 * the patch used to carry went with it. There is still no client-writable
 * `deliveredAt`.
 *
 * `lines`, when sent, is the WHOLE array the order should end up with: lines
 * keep their `id`, a line without one is new, and an omitted `id` that was
 * there before is removed.
 */
export type DeliveryOrderPatch = Partial<DeliveryOrderBody> & {
  status?: string;
  /** Stars or unstars the order. Patch only: a new order starts unstarred. */
  isCritical?: boolean;
};

/** What the order dialog emits — a full create body, plus (on an edit only) the
 *  column move above. */
export type DeliveryOrderSubmit = DeliveryOrderBody & Pick<DeliveryOrderPatch, 'status'>;

/**
 * The POST body. The column key typed `never` rather than merely omitted:
 * omitting it lets a `DeliveryOrderSubmit` through unchallenged (extra
 * properties are assignable), so "a create sends no column" would rest on a
 * comment. `never` makes the compiler the one enforcing it — the create call
 * site has to strip it, and a future edit that reintroduces a create-time
 * column fails to build instead of silently POSTing one.
 */
export type DeliveryOrderCreateBody = DeliveryOrderBody & {
  status?: never;
};

/** The toast for a failed card move: the one-open-order 409 gets its own
 *  explanation; anything else keeps the generic save error. */
export function moveCardErrorTitleKey(error: unknown): string {
  if (!(error instanceof DeliveryOrderConflictError)) return 'logistics.pipeline.saveError';
  return error.reason === 'synced-locked'
    ? 'logistics.oneOpen.statusLocked'
    : 'logistics.oneOpen.dragBlocked';
}

/** What a task's booking adds to an order already on its way: the material,
 *  how much, in the claim's own unit, and the task it comes from. The server
 *  adds it to the order's line for that material — or, when the order has no
 *  such line yet, as a new one. `quantity` is the delta: it grows the LINE
 *  and this task's share of it alike, whether the task is new to the line or
 *  already on it (contract v13). */
export type DeliveryLineBody = {
  itemName: string;
  quantity: number;
  unit: string;
  source: IDeliverySourceKey;
  /** The line the join appends to the order's dispatch note. */
  note?: string;
};

const siteBase = (siteId: string) => `/api/sites/${encodeURIComponent(siteId)}`;

/** The 401 every write shares, else the endpoint's own failure. */
async function failureOf(res: Response): Promise<ILogisticsFailure> {
  if (res.status === 401) throw new UnauthorizedError();
  return readLogisticsFailure(res);
}

/** Reads an inventory write's response: the site's levels on success, a
 *  typed error otherwise. */
async function levelsFrom(res: Response, what: string): Promise<{ levels: InventoryLevel[] }> {
  if (res.ok) return (await res.json()) as { levels: InventoryLevel[] };
  throw apiFailure(what, await failureOf(res));
}

/** The one 401 / 404 / 409 vocabulary every delivery-order write shares — a
 *  409 always carries its reason in `details.reason`, whichever rule refused
 *  the write (and an `unsynced-rival` the blocking order's ref). */
async function orderFailure(res: Response, what: string): Promise<never> {
  const failure = await failureOf(res);
  if (res.status === 404) throw new LogisticsNotFoundError(failure);
  // The order's supplier draft (spec 2026-10-25 §12) is refused on its own fields.
  const supplier = supplierRefusalOf(failure);
  if (supplier) throw supplier;
  if (res.status === 409) throw new DeliveryOrderConflictError(conflictReasonOf(failure.reason), failure);
  throw apiFailure(what, failure);
}

/** The answer every delivery-order write that returns an order is read
 *  through: the `{ order }` envelope on success, `orderFailure` otherwise. */
async function orderFrom(res: Response, what: string): Promise<Delivery> {
  if (res.ok) return ((await res.json()) as { order: Delivery }).order;
  return orderFailure(res, what);
}

/** A status write's 400 naming a `name` is the board's one-name-per-status
 *  rule — the server words it as a plain validation error. */
function statusReasonOf(failure: ILogisticsFailure): string | undefined {
  const details = failure.details as { name?: unknown } | undefined;
  return failure.status === 400 && typeof details?.name === 'string' ? 'status-name-taken' : undefined;
}

const statusesBase = (siteId: string) =>
  `/api/sites/${encodeURIComponent(siteId)}/delivery-statuses`;

// `B` is the body shape each caller asserts the endpoint returns — inferred
// from `pick`'s own parameter annotation, so the assumed shape is checked and
// self-documented at the call site rather than erased behind `never`.
async function unwrap<B, T>(res: Response, pick: (body: B) => T, what: string): Promise<T> {
  if (res.ok) return pick((await res.json()) as B);
  const failure = await failureOf(res);
  if (res.status === 404) throw new LogisticsNotFoundError(failure);
  if (res.status === 409) throw new StatusInUseError(failure.count, failure);
  throw apiFailure(what, { ...failure, reason: failure.reason ?? statusReasonOf(failure) });
}

/**
 * The logistics API (reads and writes) over an injected transport — each app
 * binds its own `authedFetch` in its `apis/logistics.ts`. Writes go through
 * the revision wrapper, so they carry the page's `X-Logistics-Revision`.
 */
export function createLogisticsApi(authedFetch: AuthedFetch) {
  const { sendLogisticsWrite } = createLogisticsWrite(authedFetch);

  async function fetchSiteLogistics(siteId: string, signal?: AbortSignal): Promise<SiteLogistics> {
    const res = await authedFetch(`${siteBase(siteId)}/logistics`, { signal });
    if (res.ok) return (await res.json()) as SiteLogistics;
    const failure = await failureOf(res);
    if (res.status === 404) throw new LogisticsNotFoundError(failure);
    if (res.status === 409) throw new LogisticsSiteNotLinkedError();
    throw apiFailure('fetch /api/sites/:siteId/logistics', failure);
  }

  async function fetchDeliveryStatuses(siteId: string, signal?: AbortSignal): Promise<DeliveryBoardStatus[]> {
    const res = await authedFetch(`${siteBase(siteId)}/delivery-statuses`, { signal });
    if (res.ok) return ((await res.json()) as { statuses: DeliveryBoardStatus[] }).statuses;
    const failure = await failureOf(res);
    if (res.status === 404) throw new LogisticsNotFoundError(failure);
    throw apiFailure('fetch delivery statuses', failure);
  }

  async function postInventorySnapshot(
    siteId: string,
    body: ISnapshotBody,
    revision: ILogisticsRevisionHandle | null,
  ): Promise<{ levels: InventoryLevel[] }> {
    const res = await sendLogisticsWrite(
      `/api/sites/${encodeURIComponent(siteId)}/inventory/snapshots`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      },
      revision,
    );
    return levelsFrom(res, 'post inventory snapshot');
  }

  async function postDeliveryOrder(
    siteId: string,
    body: DeliveryOrderCreateBody,
    revision: ILogisticsRevisionHandle | null,
  ): Promise<Delivery> {
    const res = await sendLogisticsWrite(
      `/api/sites/${encodeURIComponent(siteId)}/delivery-orders`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      },
      revision,
    );
    return orderFrom(res, 'post delivery order');
  }

  /**
   * Join a task's booking to an OPEN delivery order: the order's LINE for the
   * material grows by the booking's amount and the task joins that line's
   * `sources`. One write, no dialog — the Stock tab's alternative to raising a
   * second order, which the one-unsynced-line rule forbids anyway.
   *
   * Every refusal is a 409 with its own `reason` (`DeliverySourceConflictReason`)
   * so the box can name it: the order landed, the line measures the material
   * differently, the line has no room for another task, the material is open
   * on another order, the truck is full.
   */
  async function addDeliveryLine(
    siteId: string,
    ref: string,
    body: DeliveryLineBody,
    revision: ILogisticsRevisionHandle | null,
  ): Promise<Delivery> {
    const res = await sendLogisticsWrite(
      `/api/sites/${encodeURIComponent(siteId)}/delivery-orders/${encodeURIComponent(ref)}/lines`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      },
      revision,
    );
    return orderFrom(res, 'add delivery line');
  }

  async function patchDeliveryOrder(
    siteId: string,
    ref: string,
    patch: DeliveryOrderPatch,
    revision: ILogisticsRevisionHandle | null,
  ): Promise<Delivery> {
    const res = await sendLogisticsWrite(
      `/api/sites/${encodeURIComponent(siteId)}/delivery-orders/${encodeURIComponent(ref)}`,
      {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(patch),
      },
      revision,
    );
    return orderFrom(res, 'patch delivery order');
  }

  async function deleteDeliveryOrder(
    siteId: string,
    ref: string,
    revision: ILogisticsRevisionHandle | null,
  ): Promise<void> {
    const res = await sendLogisticsWrite(
      `/api/sites/${encodeURIComponent(siteId)}/delivery-orders/${encodeURIComponent(ref)}`,
      { method: 'DELETE' },
      revision,
    );
    if (res.ok) return; // 204 — no body
    // The same vocabulary as a patch: a synced order's 409 is the typed
    // conflict, so the page can say the order is locked.
    await orderFailure(res, 'delete delivery order');
  }

  async function deleteInventoryMaterial(
    siteId: string,
    materialName: string,
    revision: ILogisticsRevisionHandle | null,
  ): Promise<{ levels: InventoryLevel[] }> {
    const res = await sendLogisticsWrite(
      `/api/sites/${encodeURIComponent(siteId)}/inventory/materials/${encodeURIComponent(materialName)}`,
      { method: 'DELETE' },
      revision,
    );
    return levelsFrom(res, 'delete inventory material');
  }

  async function postDeliveryStatus(
    siteId: string,
    name: string,
    revision: ILogisticsRevisionHandle | null,
  ): Promise<DeliveryBoardStatus> {
    const res = await sendLogisticsWrite(
      statusesBase(siteId),
      {
        method: 'POST',
        body: JSON.stringify({ name }),
      },
      revision,
    );
    return unwrap(res, (body: { status: DeliveryBoardStatus }) => body.status, 'create delivery status');
  }

  async function renameDeliveryStatus(
    siteId: string,
    statusId: string,
    name: string,
    revision: ILogisticsRevisionHandle | null,
  ): Promise<DeliveryBoardStatus> {
    const res = await sendLogisticsWrite(
      `${statusesBase(siteId)}/${encodeURIComponent(statusId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      },
      revision,
    );
    return unwrap(res, (body: { status: DeliveryBoardStatus }) => body.status, 'rename delivery status');
  }

  async function reorderDeliveryStatuses(
    siteId: string,
    order: string[],
    revision: ILogisticsRevisionHandle | null,
  ): Promise<DeliveryBoardStatus[]> {
    const res = await sendLogisticsWrite(
      `${statusesBase(siteId)}/order`,
      {
        method: 'PATCH',
        body: JSON.stringify({ order }),
      },
      revision,
    );
    return unwrap(res, (body: { statuses: DeliveryBoardStatus[] }) => body.statuses, 'reorder delivery statuses');
  }

  async function deleteDeliveryStatus(
    siteId: string,
    statusId: string,
    revision: ILogisticsRevisionHandle | null,
  ): Promise<void> {
    const res = await sendLogisticsWrite(
      `${statusesBase(siteId)}/${encodeURIComponent(statusId)}`,
      {
        method: 'DELETE',
      },
      revision,
    );
    if (res.ok) return; // 204
    await unwrap(res, () => undefined, 'delete delivery status');
  }

  return {
    fetchSiteLogistics,
    fetchDeliveryStatuses,
    postInventorySnapshot,
    postDeliveryOrder,
    patchDeliveryOrder,
    deleteDeliveryOrder,
    addDeliveryLine,
    deleteInventoryMaterial,
    postDeliveryStatus,
    renameDeliveryStatus,
    reorderDeliveryStatuses,
    deleteDeliveryStatus,
  };
}
